/**
 * ShareContentMediaThumb — the media surface of a share-content bubble
 * (`ShareContentBubble`): the `MediaBubbleFrame` holding the shared media's
 * thumbnail, plus the centered play-icon overlay on the video variant.
 * Extracted from `ShareContentBubble` to keep that file under the
 * component-size threshold.
 *
 * Sizing rule (shared with `ImageBubble` / `VideoBubble`):
 *  `chat_screen.dart:1329-1343` —
 *      `imageWidth = aspectRatio > 1 ? width * 0.75 : width / 2`
 *      `imageHeight = imageWidth / aspectRatio`
 *  For share content the `aspectRatio` source is
 *  `chatMessage.aspectRatio ?? chatMessage.shareInfo?.mediaInfo?.aspectRatio`
 *  (`chat_screen.dart:1334-1336`). Persisted share messages typically only
 *  carry the aspect ratio inside `shareInfo.mediaInfo` — `message.aspectRatio`
 *  is set on direct image / video sends, not on shares.
 *
 * Thumbnail URL: shared media is "content" in Flutter's `UserMedia.isContent =
 * true` (`chat_screen.dart:2105-2106` / `:3233-3234`), which routes through the
 * CDN branch of `MediaUtils.getMediaThumbnailUrl` (`mediaUtils.ts:40-42`). On
 * the web we resolve the URL inline as `env.CDN_NAME + mediaInfo.thumbnail` —
 * same pattern `ImageBubble` / `VideoBubble` use for their non-content branch,
 * but with the CDN prefix to match `isContent = true`. The existing
 * `ChatUtils.getChatMessageThumbnail` helper is typed against `ChatMessageDto`
 * AND it uses `DEFAULT_BUCKET_PREFIX` (`chatUtils.ts:55`), which would be wrong
 * for share content — hence the inline resolution.
 *
 * Video variant overlays a centered play icon over the media thumbnail
 * (`chat_screen.dart:2217-2232` own / `:3320-3335` peer:
 * `Icon(Icons.play_arrow, color: AppColors.white.withValues(alpha: .7),
 * size: 40)`).
 *
 * `MediaBubbleFrame` reuse:
 *  - The frame already owns sizing (75vw / 50vw + CSS `aspect-ratio`), corner
 *    radii, placeholder, padding, and the absolutely-positioned overlay slot.
 *    The frame's `marginRight: 4` / `marginLeft: 4` gating on `isOwn` is the
 *    same Flutter `EdgeInsets.only(right: 4, top: 12)` / `(left: 4, top: 12)`
 *    used by the share-content media block (`chat_screen.dart:2168` / `:3271`).
 *    Hence we just hand the frame the resolved URL + aspect-ratio + play
 *    overlay (if video) and let it render — no duplication.
 *
 * Theme tokens:
 *  - Play overlay colour → `theme.white + 'B3'` (white at 70% alpha) — exact
 *    Flutter parity with `AppColors.white.withValues(alpha: .7)`. Same mapping
 *    `VideoBubble` uses.
 *
 * Responsive tokens (closest available on the project scale):
 *  - Play overlay size → `responsive.icon.xlarge` (= 32). Same mapping
 *    `VideoBubble` uses (Flutter spec is 40px; the project's responsive icon
 *    scale has `xlarge` = 32 and `xxlarge` = 48 surrounding it — `xlarge` is
 *    the existing project-wide overlay choice).
 *  - The media bubble corners are handled inside `MediaBubbleFrame`.
 */

import React from 'react';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { env } from '@/config/env';
import { MediaBubbleFrame } from './MediaBubbleFrame';

/**
 * `mediaType` of the shared media. Mirrors the `MediaInfo.type` field on
 * Firestore (`0` = photo, `1` = video). The dispatcher in `MessageBubble`
 * passes the value derived from the `ChatMessageType` it registered:
 *   - `ChatMessageType.sharePhoto` → `0`
 *   - `ChatMessageType.shareVideo` → `1`
 */
export type ShareContentMediaType = 0 | 1;

interface ShareContentMediaThumbProps {
  /** The share-content message whose `shareInfo.mediaInfo` is rendered. */
  message: ChatMessageEntity;
  /** Whether the message is owned by the current user. */
  isOwn: boolean;
  /** Whether this bubble is the run-end (see Flutter `isLastMessageRight` / `Left`). */
  isLastOfRun: boolean;
  /**
   * Photo or video. Drives the centered play-overlay (rendered only when
   * `mediaType === 1`).
   */
  mediaType: ShareContentMediaType;
  /**
   * `share-photo` / `share-video` — the QA token the PARENT derived for its own
   * wrapper handle. Reused here so the media handle
   * (`chat-bubble-<qaTypeToken>-media-<documentId>`) can never drift from the
   * wrapper handle (`chat-bubble-<qaTypeToken>-<documentId>`).
   */
  qaTypeToken: string;
  /** Tap handler — the parent's content-details navigation. */
  onTap: () => void;
}

export const ShareContentMediaThumb: React.FC<ShareContentMediaThumbProps> = ({
  message,
  isOwn,
  isLastOfRun,
  mediaType,
  qaTypeToken,
  onTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const mediaInfo = message.shareInfo?.mediaInfo;

  // Aspect ratio source: Flutter at `chat_screen.dart:1334-1336` reads
  //   `chatMessage.aspectRatio ?? chatMessage.shareInfo?.mediaInfo?.aspectRatio`
  // — `message.aspectRatio` is set on direct image / video sends, not on
  // shares, so the second branch is the usual source. The web port mirrors
  // the same fallback chain.
  const aspectRatio = message.aspectRatio ?? mediaInfo?.aspectRatio;

  // Shared content uses the CDN branch (`isContent = true` in Flutter — see
  // header comment). We inline the prefix rather than reusing
  // `ChatUtils.getChatMessageThumbnail` because that helper hardcodes
  // `DEFAULT_BUCKET_PREFIX`, which is wrong for shared CONTENT thumbnails.
  const thumbnailUrl = mediaInfo?.thumbnail
    ? env.CDN_NAME + mediaInfo.thumbnail
    : '';

  // Defensive uploading branch — same rationale as `ImageBubble` /
  // `VideoBubble`. Shared content should not be mid-upload by the time it's
  // sent, but the field is on the entity so we honour it.
  const isUploading = message.isUploading === true;

  // White at 70% alpha — Flutter `AppColors.white.withValues(alpha: .7)`.
  // `'B3'` is the hex form of `Math.round(0.7 * 255) = 179`. Same pattern
  // `VideoBubble` uses for the play-overlay tint.
  const playOverlayColor = `${theme.white}B3`;

  return (
    <MediaBubbleFrame
      isOwn={isOwn}
      isLastOfRun={isLastOfRun}
      aspectRatio={aspectRatio}
      // Plaintext branch (Finding 5 unchanged): an already-resolved content-CDN
      // URL, rendered directly by `CachedImage` inside the frame.
      thumbnailSource={thumbnailUrl}
      isUploading={isUploading}
      processingLabel={
        mediaType === 1 ? l10n.chat.uploading.video : l10n.chat.uploading.image
      }
      onTap={onTap}
      qa={{ id: `chat-bubble-${qaTypeToken}-media-${message.documentId}` }}
    >
      {/* Play overlay — only on the video variant, and only when there is
          actually a thumbnail to overlay (same condition `VideoBubble`
          uses; rendering a play icon on an empty placeholder looks
          broken). */}
      {mediaType === 1 && !isUploading && thumbnailUrl !== '' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: responsive.borderRadius.round,
            backgroundColor: theme.loadingOverlay,
            padding: responsive.spacing.sm,
          }}
        >
          <AssetIcon
            path="/assets/vectors/play_filled.svg"
            width={responsive.icon.xlarge}
            height={responsive.icon.xlarge}
            color={playOverlayColor}
          />
        </div>
      )}
    </MediaBubbleFrame>
  );
};
